import { z } from "zod";
/**
 * The Unity ⇄ panel `postMessage` contract (ADR-049).
 *
 * **We implement this, we did not design it.** The shapes are read from the shipped Unity build —
 * `WebViewer.jslib` and `WebFrontendBridge.cs` (ADR-044 §7) — so the field names, the flat envelope and the
 * `"x;y;z"` transform strings are theirs. Where we deviate it is deliberate and written down in ADR-049 §3.
 *
 * It lives in `@arc/contracts` because that is where wire schemas live and the package is browser-safe. It is
 * NOT a ts-rest contract: there is no HTTP here, only a window boundary.
 */
/** An id on the wire: a positive integer. Unity sends `int`, and `0` is its "none" value everywhere but one. */
const elementId = z.number().int().positive();
/**
 * A transform triple as the BACKEND stores it — `"x;y;z"`, passed through Unity verbatim
 * (`WebFrontendBridge.NotifyElementTransformChanged`). Not numbers, not a vector, not a quaternion.
 *
 * Validated by shape rather than parsed: the panel hands the string straight back to the same renderer the
 * editor uses, so parsing here would only create a second place for the format to drift.
 */
const transformTriple = z
    .string()
    .regex(/^-?\d+(\.\d+)?;-?\d+(\.\d+)?;-?\d+(\.\d+)?$/, 'expected "x;y;z"');
/**
 * Messages Unity sends US. A discriminated union **per `type`**, never one envelope with a shared id rule
 * (ADR-049 D3, plan panel) — a global "ids are positive" rule would drop `reloadView`'s documented `0`/absent
 * id at the validator, making the very fallback `reloadView` exists for unreachable.
 */
export const WebglInboundMessage = z.discriminatedUnion("type", [
    /** Unity deleted an element in the 3D editor. It has ALREADY persisted that; we only mirror. */
    z.object({ type: z.literal("elementDeleted"), elementId }),
    /** Unity selected an element for editing. `selectTab` false = focus the step without activating a tab. */
    z.object({ type: z.literal("elementSelected"), elementId, selectTab: z.boolean().optional() }),
    /** A transform patch Unity has already written. Position/rotation/size are independently optional. */
    z.object({
        type: z.literal("transformChanged"),
        elementId,
        position: transformTriple.optional(),
        rotation: transformTriple.optional(),
        size: transformTriple.optional(),
    }),
    /**
     * "Your view is stale, re-read." **`elementId` is NON-negative and optional here, unlike everywhere else**:
     * Unity documents `0`/absent as the plain-reload signal (`WebFrontendBridge.NotifyReloadView` passes `0` when
     * it has no element to re-target). Rejecting it would silently ignore a legitimate refresh and leave the panel
     * showing stale content — the opposite of the message's purpose.
     */
    z.object({ type: z.literal("reloadView"), elementId: z.number().int().nonnegative().optional() }),
    /** The user clicked Unity's canvas. Drops focus — and deliberately does NOT commit (ADR-049 D6). */
    z.object({ type: z.literal("blurEditor") }),
]);
/**
 * Messages WE send Unity. Typed so the sender cannot invent a shape the C# `JsonUtility` envelope cannot read —
 * it deserializes into a flat class, so an unknown field is dropped and a mistyped one is silently defaulted.
 */
export const WebglOutboundMessage = z.discriminatedUnion("type", [
    /** The panel finished loading the manual. Unity waits for this before sending a warm-open selection. */
    z.object({ type: z.literal("webglReady"), manualId: elementId }),
    /** The user picked an element tab in the panel. */
    z.object({ type: z.literal("elementSelected"), elementId }),
    /**
     * A setting the user changed in the panel, already persisted here (ADR-044 §8 D12).
     *
     * **One message per setting that actually MOVED**, not one per user action: `copyElementSettings` changes N
     * settings in a single call, and this is Unity's only per-setting channel — one message for N changes would
     * leave it rendering stale values for the other N−1.
     *
     * `value` is a string because the C# envelope's field is one (`WebFrontendBridge.FrontendMessage`); a cleared
     * setting sends `""`, which is what the legacy sends too. `isFile` tells Unity to re-fetch the element rather
     * than apply the value directly — file settings are deferred to Phase 2b, so it is always false for now.
     */
    z.object({
        type: z.literal("settingChanged"),
        elementId,
        manualId: elementId,
        settingName: z.string().min(1),
        value: z.string(),
        isFile: z.boolean(),
    }),
]);
/**
 * Parse an inbound payload. Returns `null` for anything that is not a message we know and trust.
 *
 * Unknown `type` is dropped **quietly on purpose**: a newer Unity build must be able to send a message an older
 * panel has never heard of without that being an error on our side. A KNOWN type with a bad payload is a
 * different thing — it means a value would reach an index — and is dropped too, but it is the case worth
 * counting in the caller.
 */
export function parseWebglInbound(raw) {
    const parsed = WebglInboundMessage.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=webgl-bridge.contract.js.map